import { i18n } from '@/localization/i18n';

// Formats a TOTAL/final duration (recording length, file length) as h:mm:ss or m:ss.
// Not meant for a live playback position — that should keep flooring, since rounding a
// position up would show a timestamp the playback hasn't reached yet.
//
// Rounds to the nearest second rather than truncating. A real-time capture pipeline
// (mic → encoder) always loses a few tens of ms to startup latency, so a recording
// stopped right at an N-second limit ends up encoded as ~(N - 0.03)s of audio — flooring
// that displayed "0:59" for a 1-minute recording (and "4:59" for a 5-minute one) instead
// of the "1:00"/"5:00" the user actually recorded.
export const millisToMinutesAndSeconds = millis => {
   const totalSeconds = Math.round(millis / 1000);
   const hours = Math.floor(totalSeconds / 3600);
   const minutes = Math.floor((totalSeconds % 3600) / 60);
   const seconds = totalSeconds % 60;

   if (hours > 0) {
      return hours + ':' + (minutes < 10 ? '0' : '') + minutes + ':' + (seconds < 10 ? '0' : '') + seconds;
   }
   return minutes + ':' + (seconds < 10 ? '0' : '') + seconds;
};

export const formatBytes = (bytes, decimals = 2) => {
   if (!+bytes) return `0 ${i18n.t('common.BYTES')}`;
   const k = 1024;
   const dm = decimals < 0 ? 0 : decimals;
   const sizes = [
      i18n.t('common.BYTES'),
      i18n.t('common.KB'),
      i18n.t('common.MB'),
      i18n.t('common.GB'),
      i18n.t('common.TB'),
      'PB',
      'EB',
      'ZB',
      'YB',
   ];
   const i = Math.floor(Math.log(bytes) / Math.log(k));
   return `${parseFloat((bytes / Math.pow(k, i)).toFixed(dm))} ${sizes[i]}`;
};

export const getThumbBase64URL = thumb => {
   if (!thumb)
      return 'data:image/png;base64,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';
   return `data:image/png;base64,${thumb}`;
};

export const dataURLtoFile = (dataurl, filename, returnAsFile = true) => {
   let dataarr = dataurl.split(','),
      mime = dataarr[0].match(/^data:(.*?);/),
      bstr = atob(dataarr[1]),
      n = bstr.length,
      u8arr = new Uint8Array(n);
   //NOSONAR
   while (n--) {
      const charbuffer = bstr.charCodeAt(n);
      u8arr[n] = charbuffer;
   }

   if (returnAsFile) {
      return new File([u8arr], filename, { type: mime[1] });
   }

   let blob = new Blob([u8arr], { type: mime[1] });
   blob['lastModifiedDate'] = new Date();
   blob['name'] = filename;
   return blob;
};

export const formatDecimal = value => {
   return new Intl.NumberFormat('en-US', {
      maximumFractionDigits: 2,
      minimumFractionDigits: value % 1 === 0 ? 0 : 2,
   }).format(value);
};

/**
 * Extract file extension from filename
 * @param {string} name - Filename
 * @returns {string} File extension with dot (e.g., ".jpg")
 */
export const getExtension = (name = '') => {
   if (!name) return '';
   const lastDot = name.lastIndexOf('.');
   if (lastDot === -1) return '';
   return name.substring(lastDot).toLowerCase();
};

/**
 * Calculates scaled dimensions for media while maintaining aspect ratio
 * @param {number} width - Original width
 * @param {number} height - Original height
 * @param {number} maxWidth - Maximum allowed width
 * @param {number} maxHeight - Maximum allowed height
 * @returns {object} { width, height }
 */
export const getMediaDimensions = (width, height, maxWidth = 350, maxHeight = 450) => {
   const w = Number(width);
   const h = Number(height);
   if (!w || !h) return { width: 300, height: 200 };
   const ratio = w / h;
   let targetWidth = w;
   let targetHeight = h;

   if (w > maxWidth) {
      targetWidth = maxWidth;
      targetHeight = targetWidth / ratio;
   }

   if (targetHeight > maxHeight) {
      targetHeight = maxHeight;
      targetWidth = targetHeight * ratio;
   }

   return { width: Math.round(targetWidth), height: Math.round(targetHeight) };
};
